'use client'

// Shared, marketing-light input + result primitives for the public /tools
// calculators. Self-contained styling (no app theme tokens) so they render
// correctly on the light marketing pages.

export function toolNum(value: string): number {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

export function toolFmt(value: number, decimals = 2): string {
  return value.toLocaleString('en-ZA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

export function toolCurrency(value: number): string {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(value)
}

export function ToolField({
  label, value, onChange, unit, hint, step = '1',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  unit: string
  hint?: string
  step?: string
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <span className="mt-1 flex overflow-hidden rounded-xl border border-slate-300 bg-white focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/20">
        <input
          type="number"
          inputMode="decimal"
          min={0}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none tabular-nums"
        />
        <span className="flex items-center border-l border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-500">{unit}</span>
      </span>
      {hint ? <span className="mt-1 block text-xs text-slate-400">{hint}</span> : null}
    </label>
  )
}

export function ToolSelect({
  label, value, onChange, options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  )
}

export function ToolResultRow({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex items-center justify-between gap-3 px-4 ${strong ? 'py-3' : 'py-2.5'}`}>
      <span className={`text-sm ${strong ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>{label}</span>
      <span className={`shrink-0 tabular-nums ${strong ? 'text-lg font-bold text-orange-600' : 'text-sm font-semibold text-slate-900'}`}>{value}</span>
    </div>
  )
}

export function ToolResultCard({ title, children, cta = true }: { title: string; children: React.ReactNode; cta?: boolean }) {
  return (
    <div className="space-y-4 md:sticky md:top-24">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
        </div>
        <div className="divide-y divide-slate-100">{children}</div>
      </div>
      {cta ? (
        <ToolMiniCta />
      ) : null}
    </div>
  )
}

function ToolMiniCta() {
  return (
    <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
      <p className="text-sm font-semibold text-slate-900">Turn this into a client-ready quote</p>
      <p className="mt-1 text-sm text-slate-600">Add labour and your margin, then send a professional estimate from your phone — free, no card.</p>
      <a
        href="/signup"
        className="mt-3 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-orange-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-orange-700"
      >
        Create a free account →
      </a>
    </div>
  )
}
